import React, { useContext, useEffect, useState, useMemo } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import { useMessage } from "../context/MessageContext";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import CommonTable from "../components/CommonTable";

const InventoryDashboard = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();

  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [balance, setBalance] = useState([]);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // Fetch branches, vendors, catalog
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [branchRes, vendorRes, catRes] = await Promise.all([
          axios.get(`/branches?orgId=${organization}`, {
            headers: { "x-auth-token": token },
          }),
          axios.get(
            `/vendors?orgId=${organization}&academicYear=${academicYear}`,
            {
              headers: { "x-auth-token": token },
            }
          ),
        ]);
        setBranches(branchRes.data || []);
        setVendors(vendorRes.data || []);
      } catch (err) {
        showMessage("Failed to load data", "error");
      }
    };

    if (organization && academicYear) {
      fetchData();
    }
  }, [organization, academicYear, showMessage]);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `/inventory/balance?organization=${organization}&academicYear=${academicYear}&branch=${selectedBranch}`,
          { headers: { "x-auth-token": token } }
        );
        setBalance(res.data || []);
      } catch {
        showMessage("Failed to load stock balance", "error");
      }
    };

    if (organization && selectedBranch) {
      fetchBalance();
    }
  }, [organization, selectedBranch, showMessage]);

  // Dynamic filters applied on client side
  const filteredBalance = useMemo(() => {
    return balance.filter((b) => {
      if (selectedVendor && b.vendor !== selectedVendor) return false;
      if (selectedGrade && !(b.grades || []).includes(selectedGrade))
        return false;
      if (selectedProgram && !(b.programs || []).includes(selectedProgram))
        return false;
      if (selectedCategory && !(b.categories || []).includes(selectedCategory))
        return false;
      return true;
    });
  }, [
    balance,
    selectedVendor,
    selectedGrade,
    selectedProgram,
    selectedCategory,
  ]);

  // Build option sets
  const allGrades = [...new Set(balance.flatMap((b) => b.grades || []))];
  const allPrograms = [...new Set(balance.flatMap((b) => b.programs || []))];
  const allCategories = [
    ...new Set(balance.flatMap((b) => b.categories || [])),
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Inventory Overview
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Branch</InputLabel>
          <Select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            label="Branch"
          >
            {branches.map((b) => (
              <MenuItem key={b._id} value={b._id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Vendor</InputLabel>
          <Select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            label="Vendor"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v._id} value={v._id}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            label="Grade"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {allGrades.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Program</InputLabel>
          <Select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            label="Program"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {allPrograms.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {allCategories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <CommonTable
        rows={filteredBalance}
        columns={[
          { label: "Item/Collection", field: "itemName" },
          {
            label: "Type",
            renderCell: (b) => (b.isCollection ? "Collection" : "Item"),
          },
          { label: "Vendor", field: "vendorName" },
          { label: "Grades", renderCell: (b) => (b.grades || []).join(", ") },
          {
            label: "Programs",
            renderCell: (b) => (b.programs || []).join(", "),
          },
          {
            label: "Categories",
            renderCell: (b) => (b.categories || []).join(", "),
          },
          { label: "Current Stock (Total)", field: "quantity" },
          { label: "For Sales", field: "saleableQuantity" },
          { label: "Teacher Sets", field: "teacherSetQuantity" },
        ]}
        hideSearch={false}
      />
    </Box>
  );
};

export default InventoryDashboard;
